<template>
	<main class="main faq-page">
		<div class="main-wrapper">
			<section class="faq-hero">
				<div class="faq-hero__glow"></div>

				<div class="faq-hero__inner _cnt">
					<div class="faq-hero__subtitle _subtitle">
						{{ $t('ВОПРОСЫ И ОТВЕТЫ') }}
					</div>

					<h1 class="faq-hero__title _h2">
						{{ $t('FAQ') }}
					</h1>
				</div>
			</section>

			<section class="faq-content">
				<div class="faq-content__inner _cnt">
					<div
						v-if="faqLoading"
						class="faq-loading"
						role="status"
						aria-live="polite"
					>
						<span class="faq-loader" aria-hidden="true"></span>

						<span class="faq-loading__text">
							{{ $t('Загрузка...') }}
						</span>
					</div>

					<div v-else-if="faqItems.length" class="faq-list">
						<article
							v-for="(item, index) in faqItems"
							:key="getFaqKey(item, index)"
							:class="[
								'faq-item',
								{
									'faq-item_active': isActivePanel(index),
								},
							]"
						>
							<button
								type="button"
								class="faq-item__question"
								:aria-expanded="isActivePanel(index) ? 'true' : 'false'"
								:aria-controls="`faq-answer-${index}`"
								@click="togglePanel(index)"
							>
								<span class="faq-item__question-text">
									{{ item.question }}
								</span>

								<span class="faq-item__toggle" aria-hidden="true">
									<span class="faq-item__toggle-line"></span>

									<span
										class="faq-item__toggle-line faq-item__toggle-line_vertical"
									></span>
								</span>
							</button>

							<transition name="faq-answer">
								<div
									v-if="isActivePanel(index)"
									:id="`faq-answer-${index}`"
									class="faq-item__answer"
								>
									<div class="faq-item__answer-inner">
										<p class="faq-item__answer-text">
											{{ item.answer }}
										</p>
									</div>
								</div>
							</transition>
						</article>
					</div>

					<div v-else class="faq-empty">
						<div class="faq-empty__icon">?</div>

						<h2 class="faq-empty__title">
							{{ $t('Вопросы пока не добавлены') }}
						</h2>

						<p class="faq-empty__text">
							{{
								$t(
									'Информация появится здесь после обновления раздела.',
								)
							}}
						</p>
					</div>
				</div>
			</section>
		</div>
	</main>
</template>

<script>
export default {
	name: 'Faq',

	props: {
		faqLoading: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			activePanel: null,
		};
	},

	computed: {
		faqItems() {
			return Array.isArray(this.$parent.faq) ? this.$parent.faq : [];
		},
	},

	watch: {
		faqItems: {
			immediate: true,

			handler(items) {
				if (!items.length) {
					this.activePanel = null;
					return;
				}

				if (this.activePanel === null || this.activePanel >= items.length) {
					this.activePanel = 0;
				}
			},
		},
	},

	methods: {
		getFaqKey(item, index) {
			return item.id || item.question || `faq-${index}`;
		},

		togglePanel(index) {
			this.activePanel = this.activePanel === index ? null : index;
		},

		isActivePanel(index) {
			return this.activePanel === index;
		},
	},
};
</script>

<style scoped lang="scss">
.main-wrapper {
	max-width: 822px;
	margin: 0 auto;
}

.faq-page {
	position: relative;
	min-height: 50vh;
}

/* Header */

.faq-hero {
	position: relative;
	padding: 44px 16px 34px;
	overflow: hidden;
}

.faq-hero__inner {
	position: relative;
	max-width: 880px;
}

.faq-hero__subtitle {
	margin-bottom: 14px;
	text-transform: uppercase;
}

.faq-hero__title {
	margin: 0;
}

/* Content */

.faq-content {
	padding: 0 16px 90px;
}

.faq-content__inner {
	max-width: 880px;
}

.faq-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

/* Loading */

.faq-loading {
	min-height: 250px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	gap: 15px;

	text-align: center;

	background: var(--bunker-86);
	border: 1px solid var(--outer-space);
	border-radius: 8px;

	box-shadow: inset 2px 2px 0 rgba(255, 255, 255, 0.018),
		inset -2px -2px 0 rgba(0, 0, 0, 0.18);
}

.faq-loader {
	width: 26px;
	height: 26px;
	display: block;

	border: 3px solid rgba(255, 255, 255, 0.12);
	border-top-color: var(--mountain-meadow);
	border-radius: 50%;

	animation: faq-loader-spin 0.75s linear infinite;
}

.faq-loading__text {
	font-family: var(--font-1);
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;

	color: var(--mantle);
}

/* FAQ item */

.faq-item {
	position: relative;
	overflow: hidden;

	background: #141a17;
	border: 1px solid #202a25;
	border-radius: 6px;

	box-shadow: 3px 3px 0 #0c0f13;

	transition: border-color 0.3s ease, background-color 0.3s ease;

	@media (any-hover: hover) {
		&:hover {
			border-color: rgba(33, 193, 126, 0.26);
		}
	}

	&_active {
		border-color: rgba(33, 193, 126, 0.28);
	}
}

.faq-item__question {
	width: 100%;
	min-height: 62px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
	margin: 0;
	padding: 20px 24px;

	text-align: left;

	color: var(--gray-nurse);
	background: transparent;
	border: 0;

	transition: color 0.25s ease, background-color 0.25s ease;

	@media (any-hover: hover) {
		&:hover {
			background: rgba(33, 193, 126, 0.018);

			.faq-item__question-text {
				color: var(--mountain-meadow);
			}
		}
	}
}

.faq-item__question-text {
	min-width: 0;

	font-family: var(--font-2);
	font-size: 17px;
	font-weight: 600;
	line-height: 120%;

	color: var(--gray-nurse);

	transition: color 0.3s ease;
}

.faq-item_active .faq-item__question {
	padding-bottom: 22px;
}

.faq-item_active .faq-item__question-text {
	color: var(--gray-nurse);
}

/* Plus/minus */

.faq-item__toggle {
	position: relative;
	flex: 0 0 18px;
	width: 18px;
	height: 18px;
	display: block;

	color: var(--mountain-meadow);
}

.faq-item__toggle-line {
	position: absolute;
	top: 50%;
	left: 50%;

	width: 10px;
	height: 2px;

	background: currentColor;

	transform: translate(-50%, -50%);

	transition: transform 0.25s ease, opacity 0.25s ease;
}

.faq-item__toggle-line_vertical {
	transform: translate(-50%, -50%) rotate(90deg);
}

.faq-item_active .faq-item__toggle-line_vertical {
	opacity: 0;

	transform: translate(-50%, -50%) rotate(0deg);
}

/* Answer */

.faq-item__answer {
	overflow: hidden;
}

.faq-item__answer-inner {
	padding: 0 24px 24px;
}

.faq-item__answer-text {
	max-width: 760px;
	margin: 0;

	font-family: var(--font-1);
	font-size: 15px;
	font-weight: 400;
	line-height: 25px;

	color: var(--edward);

	white-space: pre-line;
}

/* Empty state */

.faq-empty {
	display: flex;
	align-items: center;
	flex-direction: column;
	padding: 58px 24px;

	text-align: center;

	background: var(--bunker-86);
	border: 1px solid var(--outer-space);
	border-radius: 8px;

	box-shadow: inset 2px 2px 0 rgba(255, 255, 255, 0.018),
		inset -2px -2px 0 rgba(0, 0, 0, 0.18);
}

.faq-empty__icon {
	width: 52px;
	height: 52px;
	display: flex;
	align-items: center;
	justify-content: center;

	font-family: var(--font-3);
	font-size: 20px;
	line-height: 1;

	color: var(--mountain-meadow);
	background: var(--heavy-metal);
	border: 1px solid var(--outer-space);
	border-radius: 8px;
}

.faq-empty__title {
	margin: 20px 0 0;

	font-family: var(--font-2);
	font-size: 20px;
	font-weight: 700;
	line-height: 27px;

	color: var(--gray-nurse);
}

.faq-empty__text {
	max-width: 430px;
	margin: 9px 0 0;

	font-family: var(--font-1);
	font-size: 13px;
	line-height: 20px;

	color: var(--mantle);
}

/* Animations */

@keyframes faq-loader-spin {
	to {
		transform: rotate(360deg);
	}
}

.faq-answer-enter-active,
.faq-answer-leave-active {
	transition: opacity 0.25s ease, transform 0.25s ease;
}

.faq-answer-enter,
.faq-answer-leave-to {
	opacity: 0;

	transform: translateY(-7px);
}

/* Tablet */

@media (max-width: 991.98px) {
	.faq-hero {
		padding-top: 48px;
	}

	.faq-hero__inner,
	.faq-content__inner {
		max-width: 760px;
	}
}

/* Mobile */

@media (max-width: 767.98px) {
	.faq-hero {
		padding: 25px 0 18px;
	}

	.faq-hero__inner,
	.faq-content__inner {
		padding: 0;
	}

	.faq-hero__subtitle {
		margin-bottom: 10px;
	}

	.faq-content {
		padding: 0 0 25px;
	}

	.faq-list {
		gap: 10px;
	}

	.faq-loading {
		min-height: 210px;
	}

	.faq-item__question {
		min-height: 62px;
		gap: 16px;
		padding: 18px 19px;
	}

	.faq-item_active .faq-item__question {
		padding-bottom: 13px;
	}

	.faq-item__question-text {
		font-size: 15px;
		line-height: 21px;
	}

	.faq-item__answer-inner {
		padding: 0 19px 20px;
	}

	.faq-item__answer-text {
		font-size: 13px;
		line-height: 21px;
	}
}

@media (max-width: 480px) {
	.faq-item__question {
		padding: 16px;
	}

	.faq-item_active .faq-item__question {
		padding-bottom: 12px;
	}

	.faq-item__answer-inner {
		padding: 0 16px 17px;
	}

	.faq-item__question-text {
		font-size: 14px;
		line-height: 20px;
	}

	.faq-empty {
		padding: 44px 18px;
	}

	.faq-loading {
		min-height: 190px;
		padding: 40px 18px;
	}
}
</style>
